/**
 * API Error Utilities
 *
 * Provides error classes and helpers for handling HTTP errors
 * in a way that the ErrorBoundary can properly route them.
 *
 * @example
 * ```ts
 * const res = await rpc.api.private.tables[':id'].$get({ param: { id } });
 * if (!res.ok) {
 *   throw await createApiError(res, 'table');
 * }
 * ```
 */

import { ERROR_CODES, type ErrorCode } from '@loader/shared-types';

import { capitalize } from '@/utils/formatters';

// Re-export for convenience
export { ERROR_CODES, type ErrorCode };

/**
 * Standardized error response from API
 */
export interface ApiErrorResponse {
  error: string;
  code?: ErrorCode;
  details?: unknown;
  request_id?: string;
}

/**
 * Error class for API/HTTP errors
 *
 * Includes status code and optional error code for the ErrorBoundary
 * to show appropriate error UI.
 */
export class ApiError extends Error {
  status: number;
  code?: ErrorCode;
  details?: unknown;
  requestId?: string;

  constructor(
    message: string,
    status: number,
    options?: { code?: ErrorCode; details?: unknown; requestId?: string }
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = options?.code;
    this.details = options?.details;
    this.requestId = options?.requestId;
  }
}

/**
 * Type guard to check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Check if error is a specific error code
 */
export function isErrorCode(error: unknown, code: ErrorCode): boolean {
  return isApiError(error) && error.code === code;
}

/**
 * Create an ApiError from a fetch Response
 *
 * Extracts error details from the response body and creates
 * a properly typed ApiError for the ErrorBoundary.
 */
export async function createApiError(
  response: Response,
  resourceName?: string
): Promise<ApiError> {
  const status = response.status;

  // Try to extract error details from response body
  let errorBody: ApiErrorResponse | null = null;
  try {
    const text = await response.text();
    errorBody = JSON.parse(text) as ApiErrorResponse;
  } catch {
    // Body wasn't JSON, use defaults
  }

  const message = errorBody?.error || getDefaultMessage(status, resourceName);

  return new ApiError(message, status, {
    code: errorBody?.code,
    details: errorBody?.details,
    requestId: errorBody?.request_id,
  });
}

/**
 * Get default error message based on status code
 */
function getDefaultMessage(status: number, resourceName?: string): string {
  const resource = resourceName || 'resource';

  switch (status) {
    case 400:
      return 'Invalid request';
    case 401:
      return 'Authentication required';
    case 403:
      return `You don't have permission to access this ${resource}`;
    case 404:
      return `${capitalize(resource)} not found`;
    case 409:
      return `${capitalize(resource)} conflict - please refresh and try again`;
    case 422:
      return 'Invalid data provided';
    case 429:
      return 'Too many requests - please wait a moment';
    case 500:
      return 'Server error occurred';
    case 502:
      return 'Service temporarily unavailable';
    case 503:
      return 'Service is currently unavailable';
    case 504:
      return 'Request timed out - please try again';
    default:
      return `Failed to load ${resource}`;
  }
}
